/**
 * Page intro - Figma `6377:87027` (and `6377:88133`, `6377:88101`, `6377:88169`,
 * `6377:88779`, `6377:88331`).
 *
 * The opening block of a page: an optional eyebrow, the page title at `Desktop/Headers/h1`
 * over a measure of roughly 826px, and a short lead in body size beneath it.
 *
 * The title is an `<h1>`: this section is what opens a page, so it owns the document's
 * main heading. Every other section heading is an `<h2>`. An editor who stacks two page
 * intros on one page will produce two `<h1>`s - worth watching if it ever happens, but
 * not worth defending against with a prop nobody would set correctly.
 *
 * The heading steps down through the same scale on narrower viewports rather than
 * inventing an in-between size, because the Figma file is desktop-only.
 *
 * **The lead can sit above the title** - `leadPlacement: 'above'`, which is how
 * WecoAcademy opens (6377:88101). It is body-medium, 18px, either way.
 *
 * **`titleSize: 'large'` is Display-01**, 82px over the file's 1180 - WecoAcademy's title,
 * and MY-WCP's (6377:88782). The home page's stays h1 (3883:70247).
 */
import { Eyebrow, SectionShell, withHighlight } from '../primitives';
import type { PageIntroSection } from '../../types/sections';

export default function PageIntro({ data }: { data: PageIntroSection }) {
  const above = data.leadPlacement === 'above';
  const title = data.titleSize === 'large'
    ? 'max-w-[1180px] text-h2 md:text-h1 lg:text-display'
    : 'max-w-[826px] text-h3 md:text-h2 lg:text-h1';
  const lead = data.lead ? <p className="max-w-xl whitespace-pre-line text-body-md text-ink-strong">{data.lead}</p> : null;

  return (
    <SectionShell settings={data.settings} name="page-intro">
      <div className="flex flex-col gap-10">
        {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
        {above ? lead : null}
        <h1 className={`text-ink ${title}`}>{withHighlight(data.heading)}</h1>
        {above ? null : lead}
      </div>
    </SectionShell>
  );
}
